<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { useLandingNews } from '../composables/useLandingNews'
import { useLandingScroll } from '../composables/useLandingScroll'

const { registerReveal } = useLandingScroll()
const { featured, side, loading, isEmpty, error } = useLandingNews()

/**
 * Imagenes del backend que no resuelven: el post trae una ruta que no existe
 * en el servidor de archivos y el navegador pinta el icono de "imagen rota".
 * En ese caso se oculta la foto y se muestra el icono del post, que es lo que
 * se ve cuando la publicacion no trae imagen.
 */
const brokenImages = ref<Record<string, boolean>>({})

function markImageBroken(id: string) {
  brokenImages.value = { ...brokenImages.value, [id]: true }
}

function hasImage(id: string, url?: string | null) {
  return Boolean(url) && !brokenImages.value[id]
}

/**
 * Seccion "Blog" en formato hero.
 *
 * Fondo propio de la seccion: una imagen abstracta en verde esmeralda mas una
 * capa de textura, con el velo de color encima. Es deliberadamente distinto
 * del hero de "Sobre nosotros", que es azul marino: dos heroes seguidos en la
 * misma pagina se leerian como el mismo bloque.
 *
 * El movimiento tiene las dos capas del hero azul (ken burns lento y parallax
 * ligado al scroll). El parallax se calcula con una variable CSS
 * (`--wl-news-shift`) que consumen tanto la regla base como los keyframes, de
 * modo que el scroll y la animacion no se pelean por la propiedad `transform`.
 *
 * Con `prefers-reduced-motion` no hay movimiento: el fondo queda estatico y
 * el texto se lee igual.
 */
const section = ref<HTMLElement | null>(null)
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)')

let ticking = false

function updateShift() {
  ticking = false
  const el = section.value
  if (!el || reduceMotion.matches) return
  const rect = el.getBoundingClientRect()
  // 0 cuando la seccion entra por abajo, 1 cuando ya sale por arriba.
  const progress = (window.innerHeight - rect.top) / (window.innerHeight + rect.height)
  const shift = Math.round((progress - 0.5) * 120)
  el.style.setProperty('--wl-news-shift', `${shift}px`)
}

function onScroll() {
  if (ticking) return
  ticking = true
  window.requestAnimationFrame(updateShift)
}

onMounted(() => {
  updateShift()
  window.addEventListener('scroll', onScroll, { passive: true })
  window.addEventListener('resize', onScroll, { passive: true })
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll)
  window.removeEventListener('resize', onScroll)
})
</script>

<template>
  <section ref="section" class="wl-noticias-section wl-news-hero" id="noticias">
    <div class="wl-news-hero-bg" aria-hidden="true"></div>
    <div class="wl-news-hero-texture" aria-hidden="true"></div>
    <div class="wl-news-hero-veil" aria-hidden="true"></div>

    <div class="container">
      <div class="wl-news-hero-inner">
        <div class="section-head reveal" :ref="registerReveal">
          <div class="section-tag">Blog</div>
          <h2 class="section-title">Recursos para <span class="gradient-text">estudiantes</span></h2>
          <p class="section-desc">Guías, consejos y actualizaciones sobre los exámenes de admisión en México.</p>
        </div>

        <div
          v-if="featured.length || side.length"
          class="wl-news-grid"
          :class="{ 'wl-news-grid--single': !side.length }"
        >
          <div v-for="post in featured" :key="post.id" class="wl-news-card wl-news-featured reveal" :ref="registerReveal">
            <div class="wl-news-img" :class="post.imageClass">
              <img
                v-if="hasImage(post.id, post.imageUrl)"
                class="wl-news-photo"
                :src="post.imageUrl ?? undefined"
                :alt="post.title"
                loading="lazy"
                @error="markImageBroken(post.id)"
              >
              <span class="wl-ni-overlay"></span>
              <div v-if="!hasImage(post.id, post.imageUrl)" class="wl-ni-label">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path v-for="(path, i) in post.icon.split('|')" :key="i" :d="path" />
                </svg>
              </div>
            </div>
            <div class="wl-news-body">
              <div class="wl-news-meta">
                <span class="wl-news-cat" :class="post.categoryClass">{{ post.category }}</span>
                <span class="wl-news-date">{{ post.date }}</span>
              </div>
              <h3>{{ post.title }}</h3>
              <p>{{ post.excerpt }}</p>
              <router-link :to="post.link || '/noticias'" class="wl-news-link">
                Leer artículo
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </router-link>
            </div>
          </div>

          <div class="wl-news-side">
            <div
              v-for="(post, index) in side"
              :key="post.id"
              class="wl-news-card reveal"
              :class="`reveal-d${index + 1}`"
              :ref="registerReveal"
            >
              <div class="wl-news-img" :class="post.imageClass">
                <img
                  v-if="hasImage(post.id, post.imageUrl)"
                  class="wl-news-photo"
                  :src="post.imageUrl ?? undefined"
                  :alt="post.title"
                  loading="lazy"
                  @error="markImageBroken(post.id)"
                >
                <span class="wl-ni-overlay"></span>
                <div v-if="!hasImage(post.id, post.imageUrl)" class="wl-ni-label">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path v-for="(path, i) in post.icon.split('|')" :key="i" :d="path" />
                  </svg>
                </div>
              </div>
              <div class="wl-news-body">
                <div class="wl-news-meta">
                  <span class="wl-news-cat" :class="post.categoryClass">{{ post.category }}</span>
                  <span class="wl-news-date">{{ post.date }}</span>
                </div>
                <h3>{{ post.title }}</h3>
                <p>{{ post.excerpt }}</p>
                <router-link :to="post.link || '/noticias'" class="wl-news-link">
                  Leer artículo
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </router-link>
              </div>
            </div>
          </div>
        </div>

        <!-- Sin publicaciones en el backend: se dice con claridad, sin inventar artículos. -->
        <div v-if="isEmpty" class="wl-news-empty reveal" :ref="registerReveal">
          <div class="wl-news-empty-icon">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
              <path d="M4 4h13a1 1 0 011 1v14H5a1 1 0 01-1-1V4z" />
              <path d="M18 8h2v9a1 1 0 01-1 1h-1" />
              <path d="M7 8h7M7 12h7M7 16h4" />
            </svg>
          </div>
          <h3>{{ error ? 'No pudimos cargar las publicaciones' : 'Pronto publicaremos nuevos artículos' }}</h3>
          <p>
            {{ error
              ? 'Vuelve a cargar la página en unos minutos. Mientras tanto, puedes explorar los simuladores disponibles.'
              : 'Todavía no hay publicaciones en el blog. Vuelve pronto para encontrar guías, consejos y novedades de la plataforma.' }}
          </p>
          <router-link to="/cursos" class="btn btn-outline-primary">Explorar simuladores</router-link>
        </div>

        <div v-if="featured.length || side.length" class="section-cta reveal" :ref="registerReveal">
          <router-link to="/noticias" class="btn btn-outline-primary">Ver todas las publicaciones</router-link>
        </div>
      </div>
    </div>
  </section>
</template>
